import { useParams, Link } from 'react-router-dom';
import { getComicById, getRelatedComics } from '@/mocks/comics';
import ComicCard from '@/components/base/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comic = id ? getComicById(id) : undefined;

  if (!comic) {
    return (
      <div className="pt-24 pb-20 text-center px-4">
        <SEOHead title="漫画未找到 - 韩漫网" description="您访问的漫画页面不存在。" />
        <h2 className="font-heading text-2xl text-foreground-800">该漫画不存在</h2>
        <Link to="/" className="inline-block mt-4 px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm hover:bg-primary-600 transition-colors whitespace-nowrap">返回首页</Link>
      </div>
    );
  }

  const relatedComics = getRelatedComics(comic, 4);
  const formatViews = (views: number): string => {
    if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
    return views.toString();
  };

  return (
    <>
      <SEOHead
        title={`${comic.title} - ${comic.author} - 免费韩国漫画在线阅读 - 韩漫网`}
        description={`${comic.title}是由${comic.author}创作的${comic.category}题材韩国漫画。${comic.description.substring(0, 100)}... 免费在线阅读最新章节。`}
        keywords={`${comic.title},${comic.author},${comic.category}韩漫,韩国漫画,免费在线阅读`}
        canonicalUrl={`https://hanman.readdy.ai/comic/${comic.id}`}
        ogType="article"
      />
      <SchemaOrg
        type="Article"
        article={{
          headline: `${comic.title} - ${comic.category}韩国漫画`,
          description: comic.description,
          author: comic.author,
          datePublished: comic.chapters[0]?.updateDate || comic.updateDate,
          dateModified: comic.updateDate,
          image: comic.cover,
        }}
      />

      <div className="pt-20">
        <Breadcrumb items={[
          { label: '首页', href: '/' },
          { label: comic.category, href: `/category/${comic.categorySlug}` },
          { label: comic.title },
        ]} />

        <article className="py-8 md:py-12 px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="md:w-[280px] flex-shrink-0">
                <div className="aspect-[3/4] overflow-hidden rounded-lg bg-secondary-100 sticky top-24">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - ${comic.author}创作的${comic.category}韩国漫画封面`}
                    title={comic.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className={`text-xs px-2.5 py-1 rounded-full ${
                    comic.status === '连载中' ? 'bg-primary-100 text-primary-700' : 'bg-secondary-200 text-secondary-700'
                  }`}>
                    {comic.status}
                  </span>
                  {comic.tags.map((tag) => (
                    <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700">
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 mb-3">
                  {comic.title}
                </h1>

                <div className="flex items-center gap-4 flex-wrap mb-6">
                  <span className="text-sm text-foreground-600">
                    作者：<strong className="text-foreground-800">{comic.author}</strong>
                  </span>
                  <span className="text-sm text-foreground-400">|</span>
                  <span className="flex items-center gap-1 text-sm text-accent-600">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-sm"></i>
                    </div>
                    <strong>{comic.rating}</strong> 分
                  </span>
                  <span className="text-sm text-foreground-400">|</span>
                  <span className="text-sm text-foreground-600">
                    {formatViews(comic.views)} 次阅读
                  </span>
                  <span className="text-sm text-foreground-400">|</span>
                  <span className="text-sm text-foreground-600">
                    共 {comic.chapters.length} 话
                  </span>
                </div>

                <div className="bg-secondary-50 rounded-lg p-5 mb-8">
                  <h2 className="font-heading text-lg text-foreground-800 mb-3">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                <div className="flex items-center gap-3 mb-6">
                  <button className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                    开始阅读 第1话
                  </button>
                  <button className="px-6 py-2.5 bg-secondary-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer">
                    加入收藏
                  </button>
                </div>

                <div>
                  <h2 className="font-heading text-lg text-foreground-800 mb-4">章节目录</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {comic.chapters.map((chapter) => (
                      <Link
                        key={chapter.id}
                        to={`/comic/${comic.id}`}
                        className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-50 border border-secondary-200/70 hover:border-primary-300 hover:bg-primary-50/30 transition-all cursor-pointer"
                      >
                        <span className="text-sm text-foreground-700 line-clamp-1">{chapter.title}</span>
                        <span className="text-xs text-foreground-400 ml-2 whitespace-nowrap">{chapter.updateDate}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {relatedComics.length > 0 && (
              <section className="mt-16">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading text-xl text-foreground-950">相关推荐</h2>
                  <Link
                    to={`/category/${comic.categorySlug}`}
                    className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
                  >
                    更多{comic.category}漫画
                    <span className="ml-1">&gt;</span>
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {relatedComics.map((rc) => (
                    <ComicCard key={rc.id} comic={rc} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </div>
    </>
  );
}